/**
 * Wishlist business logic. Owns the in-memory item list, dedupes by product+variant,
 * persists via LalumeWishlistStorage, and notifies subscribers on change.
 *
 * Item shape: { productId, variantId, handle, quantity, addedAt }
 * (productId / variantId stored as strings for reliable comparison.)
 *
 * This layer knows nothing about the DOM or localStorage directly, so the persistence
 * layer (wishlist-storage.js) can later be swapped for account/cross-device syncing
 * without changing anything in here or in wishlist-ui.js.
 */
(function () {
  'use strict';

  var storage = window.LalumeWishlistStorage;
  var items = storage.load();
  var listeners = [];

  function keyOf(productId, variantId) {
    return String(productId) + ':' + String(variantId);
  }

  function find(productId, variantId) {
    var key = keyOf(productId, variantId);
    for (var i = 0; i < items.length; i++) {
      if (keyOf(items[i].productId, items[i].variantId) === key) return items[i];
    }
    return null;
  }

  function persistAndNotify() {
    storage.save(items);
    var snapshot = items.slice();
    listeners.forEach(function (fn) {
      try {
        fn(snapshot);
      } catch (e) {
        /* a subscriber error must never break the wishlist */
      }
    });
    document.dispatchEvent(
      new CustomEvent('wishlist:updated', {
        bubbles: true,
        detail: { items: snapshot }
      })
    );
  }

  function has(productId, variantId) {
    return !!find(productId, variantId);
  }

  function add(productId, variantId, handle, quantity) {
    quantity = Math.max(1, parseInt(quantity, 10) || 1);
    var existing = find(productId, variantId);
    if (existing) {
      existing.quantity = quantity;
    } else {
      items.push({
        productId: String(productId),
        variantId: String(variantId),
        handle: handle,
        quantity: quantity,
        addedAt: Date.now()
      });
    }
    persistAndNotify();
  }

  function remove(productId, variantId) {
    var key = keyOf(productId, variantId);
    items = items.filter(function (it) {
      return keyOf(it.productId, it.variantId) !== key;
    });
    persistAndNotify();
  }

  function toggle(productId, variantId, handle) {
    if (has(productId, variantId)) {
      remove(productId, variantId);
      return false;
    }
    add(productId, variantId, handle, 1);
    return true;
  }

  function setQuantity(productId, variantId, quantity) {
    var existing = find(productId, variantId);
    if (!existing) return;
    existing.quantity = Math.max(1, parseInt(quantity, 10) || 1);
    persistAndNotify();
  }

  /**
   * Swaps the variant on an existing wishlist entry (e.g. changing Material from the
   * wishlist page). If the target variant is already a separate wishlist entry, the two
   * are merged rather than allowing a duplicate row.
   */
  function changeVariant(productId, oldVariantId, newVariantId) {
    var existing = find(productId, oldVariantId);
    if (!existing) return;
    if (String(oldVariantId) === String(newVariantId)) return;

    var conflict = find(productId, newVariantId);
    if (conflict) {
      items = items.filter(function (it) {
        return it !== existing;
      });
    } else {
      existing.variantId = String(newVariantId);
    }
    persistAndNotify();
  }

  function removeAll() {
    items = [];
    persistAndNotify();
  }

  function getAll() {
    return items.slice();
  }

  function count() {
    return items.length;
  }

  function subscribe(fn) {
    listeners.push(fn);
    return function unsubscribe() {
      listeners = listeners.filter(function (l) {
        return l !== fn;
      });
    };
  }

  window.LalumeWishlistService = {
    add: add,
    remove: remove,
    toggle: toggle,
    has: has,
    find: find,
    setQuantity: setQuantity,
    changeVariant: changeVariant,
    removeAll: removeAll,
    getAll: getAll,
    count: count,
    subscribe: subscribe
  };
})();
